import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import { appSettingsFixture, applyAppSettingsPatch, resetAppSetting } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('app-settings');

/**
 * Normalisiert die echte Core-Shape auf die Form, die SettingsCard/SettingsField
 * erwarten. Spec: lmm-wiki/wiki/orbit-specs/admin-integrations-and-settings-shapes.md
 *
 * Core liefert:
 *   { settings: [{ key, type, group, label, description, min, max,
 *                  isSecret, envDefault, effective, source, updatedAt }] }
 *
 * SettingsField erwartet:
 *   { key, type, value, dbOverridden, envDefault, description, ... }
 *
 * - `effective` → `value`
 * - `source === 'db'` → `dbOverridden: true`
 * - `isSecret: true` → `type: 'secret'` (überschreibt den Original-Type)
 * - `integer` → `number`
 *
 * Gruppiert anschließend nach `group`-Feld für die Card-pro-Gruppe-Darstellung.
 */
function normalizeAppSettings(raw) {
  dlog('raw', raw);
  if (!raw) return { groups: [], raw };
  const list = Array.isArray(raw) ? raw : raw.settings ?? [];
  const groupMap = new Map();
  for (const s of list) {
    const mapped = {
      key: s.key,
      type: s.isSecret ? 'secret' : mapType(s.type),
      value: s.effective,
      envDefault: s.envDefault,
      dbOverridden: s.source === 'db',
      description: s.description,
      label: s.label,
      min: s.min ?? null,
      max: s.max ?? null,
      isSecret: !!s.isSecret,
      updatedAt: s.updatedAt ?? null,
    };
    const group = s.group ?? 'Sonstiges';
    if (!groupMap.has(group)) {
      groupMap.set(group, { title: group, settings: [] });
    }
    groupMap.get(group).settings.push(mapped);
  }
  return { groups: Array.from(groupMap.values()), raw };
}

function mapType(coreType) {
  switch (coreType) {
    case 'integer': return 'number';
    case 'boolean': return 'boolean';
    case 'url': return 'url';
    case 'string': return 'string';
    default: return 'string';
  }
}

/** GET /v1/admin/settings — flache typisierte Liste, Core-Spec. */
export function useAppSettings() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['appSettings', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return normalizeAppSettings(appSettingsFixture());
      }
      const res = await fetch('/v1/admin/settings');
      return normalizeAppSettings(res);
    },
    staleTime: 30_000,
  });
}

/**
 * PUT /v1/admin/settings/:key — pro Key ein Call, Body `{ value }`.
 *
 * SettingsCard ruft `onSave(patch)` mit einem `{key: value}`-Object aller
 * dirty Felder. Wir fanen das clientseitig in N parallele PUTs aus.
 */
export function useSaveAppSettings() {
  return useApiMutation({
    live: async (fetch, patch) => {
      const entries = Object.entries(patch);
      const results = await Promise.all(
        entries.map(([key, value]) =>
          fetch(`/v1/admin/settings/${encodeURIComponent(key)}`, {
            method: 'PUT',
            body: { value },
          }),
        ),
      );
      return { updated: results.length, results };
    },
    fixture: (patch) => {
      applyAppSettingsPatch(patch);
      return { updated: Object.keys(patch).length };
    },
    invalidate: ['appSettings'],
    successMessage: (res) =>
      res?.updated ? `${res.updated} Setting(s) gespeichert.` : 'Gespeichert.',
  });
}

/** DELETE /v1/admin/settings/:key — DB-Override entfernen → env-Default aktiv. */
export function useResetAppSetting() {
  return useApiMutation({
    live: (fetch, { key }) =>
      fetch(`/v1/admin/settings/${encodeURIComponent(key)}`, { method: 'DELETE' }),
    fixture: ({ key }) => {
      resetAppSetting(key);
      return { ok: true };
    },
    invalidate: ['appSettings'],
    successMessage: (_, { key }) => `${key}: fällt zurück auf env-Default.`,
  });
}
